import { DesktopWindowsOutlined, PhoneIphoneOutlined } from '@mui/icons-material'
import { Stack, Typography } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { EmployeeBox, FlexBox, GapBox } from 'components/Box'
import { BoxForm, NumberField, Select, Switch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { adDisplaysBeforeRefreshValues } from 'features/inventory/ad-unit/types/adDisplaysBeforeRefresh'
import { numberOfRotatingAdsValues } from 'features/inventory/ad-unit/types/numberOfRotatingAds'
import { useAbility } from 'providers/casl'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'

import type { Schema } from '../schema'
import type { SelectOption } from 'components/Select'
import type { AdDisplaysBeforeRefresh } from 'features/inventory/ad-unit/types/adDisplaysBeforeRefresh'
import type { NumberOfRotatingAds } from 'features/inventory/ad-unit/types/numberOfRotatingAds'

export const MultiFormatsSection = (): React.JSX.Element => {
  const { t } = useTranslation('features/adUnit', {
    keyPrefix: 'form.unfilledRecovery.multiFormats',
  })
  const { t: tc } = useTranslation('common')

  const { control, watch } = useFormContext<Schema>()
  const { allowMultiformatEnabled } = watch()
  const ability = useAbility()

  const cannotUpdate: boolean =
    ability.cannot('update', 'AdUnit') ||
    ability.cannot('update', 'AdUnitFeature-unfilledRecoveryMultiFormats') ||
    ability.cannot('update', 'Product-unfilledRecovery')

  const DEVICE_CONTAINER_SPACING = 8 // [px]

  return (
    <Paper data-cy='section-multi-formats'>
      <SectionHeader
        subtitle={t('description')}
        title={t('title')}
        titleEndAdornment={
          <Switch
            control={control}
            disabled={
              ability.cannot('update', 'AdUnit') ||
              ability.cannot('update', 'Product-unfilledRecovery')
            }
            name='allowMultiformatEnabled'
            tooltip={{ content: t('tooltip') }}
          />
        }
      />

      <GapBox />

      {ability.can('read', 'AdUnitFeature-unfilledRecoveryMultiFormats') &&
        allowMultiformatEnabled && (
          <EmployeeBox>
            <BoxForm>
              <Select<Schema, NumberOfRotatingAds>
                control={control}
                disabled={cannotUpdate}
                label={t('numberOfRotatingAds.label')}
                name='rotatingAdsQty'
                options={numberOfRotatingAdsValues.map(
                  (value: NumberOfRotatingAds): SelectOption<NumberOfRotatingAds> => ({
                    label: value,
                    value,
                  }),
                )}
                tooltip={{ content: t('numberOfRotatingAds.tooltip') }}
              />

              <Stack
                direction='row'
                spacing={DEVICE_CONTAINER_SPACING}
                sx={{
                  ml: CONTAINER_SPACING,
                }}
              >
                <BoxForm>
                  <FlexBox
                    axis='x'
                    sx={{ gap: 1 }}
                  >
                    <DesktopWindowsOutlined
                      data-cy='desktop-windows-outlined-icon'
                      fontSize='inherit'
                    />
                    <Typography>{tc('device.desktop')}</Typography>
                  </FlexBox>

                  <NumberField
                    control={control}
                    disabled={cannotUpdate}
                    label={t('columns.singleAdViewTime.label')}
                    name='desktopSingleAdViewTime'
                    tooltip={{ content: t('columns.singleAdViewTime.tooltip') }}
                    width='md'
                  />

                  <Select<Schema, AdDisplaysBeforeRefresh>
                    control={control}
                    disabled={cannotUpdate}
                    label={t('columns.adDisplaysBeforeRefresh.label')}
                    name='desktopAdDisplaysBeforeRefresh'
                    options={adDisplaysBeforeRefreshValues.map(
                      (value: AdDisplaysBeforeRefresh): SelectOption<AdDisplaysBeforeRefresh> => ({
                        label: value,
                        value,
                      }),
                    )}
                    tooltip={{ content: t('columns.adDisplaysBeforeRefresh.tooltip') }}
                  />
                </BoxForm>

                <BoxForm>
                  <FlexBox
                    axis='x'
                    sx={{ gap: 1 }}
                  >
                    <PhoneIphoneOutlined
                      data-cy='phone-iphone-outlined-icon'
                      fontSize='inherit'
                    />
                    <Typography>{tc('device.mobile')}</Typography>
                  </FlexBox>

                  <NumberField
                    control={control}
                    disabled={cannotUpdate}
                    label={t('columns.singleAdViewTime.label')}
                    name='mobileSingleAdViewTime'
                    tooltip={{ content: t('columns.singleAdViewTime.tooltip') }}
                    width='md'
                  />

                  <Select<Schema, AdDisplaysBeforeRefresh>
                    control={control}
                    disabled={cannotUpdate}
                    label={t('columns.adDisplaysBeforeRefresh.label')}
                    name='mobileAdDisplaysBeforeRefresh'
                    options={adDisplaysBeforeRefreshValues.map(
                      (value: AdDisplaysBeforeRefresh): SelectOption<AdDisplaysBeforeRefresh> => ({
                        label: value,
                        value,
                      }),
                    )}
                    tooltip={{ content: t('columns.adDisplaysBeforeRefresh.tooltip') }}
                  />
                </BoxForm>
              </Stack>
            </BoxForm>
          </EmployeeBox>
        )}
    </Paper>
  )
}
